import {
  BuildTrackerPreviewDataInput,
  DeepPartial,
  Fulfillment,
  OrderDataInterface,
  OrderLocationPoint,
  TrackerTimelineItem,
} from './types'

const DEFAULT_PRIMARY_COLOR = '#1F5EA9'
const DEFAULT_SECONDARY_COLOR = '#D8E6F7'

export function normalizeApiBaseUrl(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/+$/, '')
}

export function addMinutes(value: Date, minutes: number): Date {
  return new Date(value.getTime() + minutes * 60 * 1000)
}

function formatClock(value?: string): string {
  if (!value) {
    return ''
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function shouldBuffer(data: OrderDataInterface) {
  return !data?.fulfillment?.runner_picked_up
}

export function getBufferedDropoffDate(data: OrderDataInterface): Date | null {
  const runnerEstimatedDropoff = data.fulfillment?.runner_estimated_dropoff

  if (!runnerEstimatedDropoff) {
    return null
  }

  const date = new Date(runnerEstimatedDropoff)
  const bufferMinutes = shouldBuffer(data)
    ? data?.merchant?.tracker_delivery_buffer || 0
    : 0

  return addMinutes(date, bufferMinutes)
}

export function formatEstimatedTime(data: OrderDataInterface) {
  const buffered = getBufferedDropoffDate(data)

  if (!buffered) {
    return false
  }

  return formatClock(buffered.toISOString()).toLowerCase()
}

export function formatEstimatedRange(data: OrderDataInterface) {
  const runnerEstimatedDropoff = data?.fulfillment?.runner_estimated_dropoff

  if (!runnerEstimatedDropoff) {
    return false
  }

  const dropoffTime = new Date(runnerEstimatedDropoff)
  const bufferedDropoff = addMinutes(
    dropoffTime,
    shouldBuffer(data) ? data?.merchant?.tracker_delivery_buffer || 0 : 0
  )
  const now = new Date()
  let minutesUntilDropoff = Math.floor(
    (bufferedDropoff.getTime() - now.getTime()) / 60000
  )

  if (bufferedDropoff < now) {
    minutesUntilDropoff = 10
  }

  return `${minutesUntilDropoff} - ${minutesUntilDropoff + 5} minutes`
}

export function buildTimelineItems(
  fulfillment?: Fulfillment
): TrackerTimelineItem[] {
  if (!fulfillment) {
    return []
  }

  const timelineEvents = [
    {
      statusText: 'Order Placed',
      keys: ['created_at', 'pending_assign'],
    },
    {
      statusText: 'En Route to Pickup',
      keys: ['runner_assigned_at', 'en_route_pickup'],
    },
    {
      statusText: 'Arrived at Pickup',
      keys: ['runner_arrived_pickup', 'arrived_pickup'],
    },
    {
      statusText: 'En Route to You',
      keys: ['runner_picked_up', 'picked_up'],
    },
    {
      statusText: 'Arriving Now',
      keys: ['runner_arrived_dropoff', 'arrived_dropoff'],
    },
    {
      statusText: 'Delivered',
      keys: ['runner_dropped_off', 'completed'],
    },
  ]

  if (fulfillment.status === 'cancelled') {
    timelineEvents.push({
      statusText: 'Cancelled',
      keys: ['cancelled'],
    })
  }

  return timelineEvents
    .map(event => ({
      statusText: event.statusText,
      statusTime:
        event.keys.map(key => fulfillment[key]).find(Boolean) || '',
    }))
    .filter(event => event.statusTime || event.statusText === 'Cancelled')
}

export function getCoordinates(
  point?: OrderLocationPoint
): [number, number] | null {
  const coordinates = point?.coordinates

  if (!coordinates || coordinates.length < 2) {
    return null
  }

  const longitude = Number(coordinates[0])
  const latitude = Number(coordinates[1])

  if (Number.isNaN(longitude) || Number.isNaN(latitude)) {
    return null
  }

  return [longitude, latitude]
}

export function deepMerge<T>(base: T, patch?: DeepPartial<T>): T {
  if (!patch) {
    return base
  }

  const output = { ...base } as Record<string, unknown>

  Object.entries(patch).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      output[key] = value
      return
    }

    if (value && typeof value === 'object') {
      const currentValue = output[key]
      output[key] = deepMerge(
        (currentValue && typeof currentValue === 'object'
          ? currentValue
          : {}) as Record<string, unknown>,
        value as DeepPartial<Record<string, unknown>>
      )
      return
    }

    output[key] = value
  })

  return output as T
}

function buildPreviewFulfillment(status: string, now: Date, etaAt: string) {
  const createdAt = addMinutes(now, -32).toISOString()
  const assignedAt = addMinutes(now, -24).toISOString()
  const arrivedPickupAt = addMinutes(now, -18).toISOString()
  const pickedUpAt = addMinutes(now, -10).toISOString()
  const arrivedDropoffAt = addMinutes(now, -1).toISOString()
  const droppedOffAt = addMinutes(now, 1).toISOString()

  const base: Fulfillment = {
    status,
    created_at: createdAt,
    runner_est_pickup_at: addMinutes(now, -12).toISOString(),
    runner_estimated_dropoff: etaAt,
  }

  if (['pending_vendor', 'pending_dispatch', 'staging', 'pending_reassign'].includes(status)) {
    return base
  }

  if (status === 'cancelled') {
    return {
      ...base,
      cancelled: addMinutes(now, -4).toISOString(),
    }
  }

  base.runner_assigned_at = assignedAt

  if (status === 'runner_assigned') {
    return base
  }

  base.runner_arrived_pickup = arrivedPickupAt

  if (status === 'runner_arrived_pickup') {
    return base
  }

  base.runner_picked_up = pickedUpAt

  if (['runner_picked_up', 'in_progress'].includes(status)) {
    return base
  }

  base.runner_arrived_dropoff = arrivedDropoffAt

  if (status === 'runner_arrived_dropoff') {
    return base
  }

  return {
    ...base,
    status: 'completed',
    runner_dropped_off: droppedOffAt,
  }
}

export function buildTrackerPreviewData(
  input: BuildTrackerPreviewDataInput = {}
): OrderDataInterface {
  const now = input.now ? new Date(input.now) : new Date()
  const trackingSettings = input.trackingSettings || {}
  const operator = input.operator || {}
  const previewStatus = trackingSettings.status || 'in_progress'
  const estimatedDropoffAt =
    trackingSettings.estimated_dropoff_at || addMinutes(now, 18).toISOString()

  const base: OrderDataInterface = {
    delivery_service: {
      name:
        trackingSettings.delivery_service_name || operator.name || 'First Delivery',
      icon: trackingSettings.logo_url || '',
      colors: {
        primary: trackingSettings.primary_color || DEFAULT_PRIMARY_COLOR,
        secondary: trackingSettings.secondary_color || DEFAULT_SECONDARY_COLOR,
      },
      footer_image: trackingSettings.footer_image || '',
      footer_link: trackingSettings.footer_link || '',
    },
    merchant: {
      id: 'preview-merchant',
      name: trackingSettings.merchant_name || 'Habitat Kitchen',
      address:
        trackingSettings.merchant_address ||
        '123 Market Street, San Francisco, CA',
      phone: trackingSettings.merchant_phone || '(555) 111-2222',
      territory: trackingSettings.merchant_territory || 'Preview Territory',
      location: {
        type: 'Point',
        coordinates:
          trackingSettings.merchant_coordinates || [-122.3999, 37.7936],
      },
      tracker_delivery_buffer: trackingSettings.tracker_delivery_buffer || 5,
    },
    customer: {
      name: trackingSettings.customer_name || 'Taylor Customer',
      address:
        trackingSettings.customer_address ||
        '456 Mission Street, San Francisco, CA',
      delivery_instructions:
        trackingSettings.customer_instructions || 'Leave at front desk',
      location: {
        type: 'Point',
        coordinates:
          trackingSettings.customer_coordinates || [-122.4017, 37.7878],
      },
    },
    runner: {
      name: trackingSettings.runner_name || 'Jordan Driver',
      phone: trackingSettings.runner_phone || '(555) 333-4444',
      location: {
        type: 'Point',
        coordinates:
          trackingSettings.runner_coordinates || [-122.4008, 37.7905],
      },
    },
    order: {
      id: 'preview-order',
      order_number: String(trackingSettings.order_number || '10018'),
      details: [
        { id: 'item-1', name: 'Burger', quantity: 1, price: 12.5 },
        { id: 'item-2', name: 'Fries', quantity: 1, price: 4.5 },
        { id: 'item-3', name: 'Soda', quantity: 1, price: 2.95 },
      ],
      special_instructions: 'No onions',
      payment: {
        subtotal: trackingSettings.subtotal || 19.95,
        tip: trackingSettings.tip || 4,
      },
    },
    fulfillment: buildPreviewFulfillment(previewStatus, now, estimatedDropoffAt),
    operator: {
      customer_support_tracking: operator.customer_support_tracking || false,
      atlas: operator.atlas || '',
      country: operator.country,
      id: operator.id,
      name:
        operator.name || trackingSettings.delivery_service_name || 'First Delivery',
    },
  }

  return deepMerge(base, input.overrides)
}
